"use client"

import { useState } from "react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sparkles,
  Loader2,
  Save,
  Send,
  Copy,
  Ban,
  ChevronRight,
  Building2,
  User,
  FileText,
  CheckCircle2,
} from "lucide-react"
import { painPoints } from "@/lib/mock-data"
import type { PainPoint } from "@/lib/types"

interface AIMappingResult {
  useCaseName: string
  candidateSolution: string
  aiType: string
  automationType: string
  requiredData: string[]
  suggestedDataClassification: string
  expectedTimeSaving: string
  expectedCostSaving: string
  expectedRiskReduction: string
  feasibilityQuestions: string[]
  suggestedDependencies: string[]
  aiOwner: string
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  )
}

function EditableField({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  multiline?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="text-sm resize-none"
        />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="text-sm" />
      )}
    </div>
  )
}

export function AIMappingWorkspace() {
  const [selectedPainPoint, setSelectedPainPoint] = useState<PainPoint | null>(null)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [mappingResult, setMappingResult] = useState<AIMappingResult | null>(null)
  const [editedMapping, setEditedMapping] = useState<Partial<AIMappingResult>>({})

  const statusColors: Record<string, string> = {
    Draft: "bg-gray-100 text-gray-700",
    Submitted: "bg-blue-100 text-blue-800",
    Mapped: "bg-emerald-100 text-emerald-800",
  }

  async function handleGenerate() {
    if (!selectedPainPoint) return
    setGenerating(true)
    try {
      const res = await fetch("http://95.217.189.103:5000/api/ai-mapping/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          painPointId: selectedPainPoint.id,
          businessProblem: selectedPainPoint.businessProblem,
          currentWorkflow: selectedPainPoint.currentWorkflow,
          systemsInvolved: selectedPainPoint.systemsInvolved,
          dataInvolved: selectedPainPoint.dataInvolved,
        }),
      })
      const data = await res.json()
      setMappingResult(data)
      setEditedMapping(data)
      toast.success("AI mapping generated successfully.")
    } catch {
      toast.error("Failed to generate AI mapping.")
    } finally {
      setGenerating(false)
    }
  }

  async function handleSave() {
    if (!mappingResult) return
    setSaving(true)
    try {
      const res = await fetch("http://95.217.189.103:5000/api/ai-mapping/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ painPointId: selectedPainPoint?.id, ...editedMapping }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success(`Mapping saved. Use Case ID: ${data.useCaseId}`)
      }
    } catch {
      toast.error("Failed to save mapping.")
    } finally {
      setSaving(false)
    }
  }

  function updateField(field: keyof AIMappingResult, value: string) {
    setEditedMapping((prev) => ({ ...prev, [field]: value }))
  }

  const displayed = { ...mappingResult, ...editedMapping } as AIMappingResult

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left panel: pain point list */}
      <div className="w-72 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Pain Points</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Select one to map to a use case</p>
        </div>
        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-0 p-2">
            {painPoints.map((pp) => {
              const isSelected = selectedPainPoint?.id === pp.id
              return (
                <button
                  key={pp.id}
                  onClick={() => {
                    setSelectedPainPoint(pp)
                    setMappingResult(null)
                    setEditedMapping({})
                  }}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex flex-col gap-1.5
                    ${isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted/70"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-mono ${isSelected ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {pp.id}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${isSelected ? "bg-primary-foreground/20 text-primary-foreground" : statusColors[pp.status]}`}>
                      {pp.status}
                    </span>
                  </div>
                  <span className={`text-xs font-medium leading-snug line-clamp-2 ${isSelected ? "text-primary-foreground" : "text-foreground"}`}>
                    {pp.processName}
                  </span>
                  <span className={`text-xs ${isSelected ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {pp.department}
                  </span>
                  {isSelected && <ChevronRight className="size-3 self-end text-primary-foreground/70" />}
                </button>
              )
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Right panel: mapping workspace */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selectedPainPoint ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <div className="size-14 rounded-full bg-muted flex items-center justify-center">
              <Sparkles className="size-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Select a Pain Point</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Choose a pain point from the left panel to begin AI mapping.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5 max-w-3xl">
            {/* Pain point summary */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm">Original Pain Point</CardTitle>
                  <Badge variant="outline" className="text-xs font-mono">{selectedPainPoint.id}</Badge>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <Field label="Department" value={
                  <span className="flex items-center gap-1.5"><Building2 className="size-3.5 text-muted-foreground" />{selectedPainPoint.department}</span>
                } />
                <Field label="Owner" value={
                  <span className="flex items-center gap-1.5"><User className="size-3.5 text-muted-foreground" />{selectedPainPoint.businessOwner}</span>
                } />
                <Field label="Process" value={selectedPainPoint.processName} />
                <Field label="Urgency" value={
                  <span className={`text-xs px-2 py-0.5 rounded font-medium border
                    ${selectedPainPoint.urgency === "Critical" ? "bg-red-100 text-red-800 border-red-200" :
                      selectedPainPoint.urgency === "High" ? "bg-orange-100 text-orange-800 border-orange-200" :
                      selectedPainPoint.urgency === "Medium" ? "bg-amber-100 text-amber-800 border-amber-200" :
                      "bg-gray-100 text-gray-700 border-gray-200"}`}>
                    {selectedPainPoint.urgency}
                  </span>
                } />
                <div className="col-span-2">
                  <Field label="Problem" value={selectedPainPoint.businessProblem} />
                </div>
                <div className="col-span-2">
                  <Field label="Systems Involved" value={
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {selectedPainPoint.systemsInvolved.map((s) => (
                        <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                      ))}
                    </div>
                  } />
                </div>
                <div className="col-span-2">
                  <Field label="Data Involved" value={selectedPainPoint.dataInvolved} />
                </div>
              </CardContent>
            </Card>

            {/* Generate button */}
            <div className="flex items-center gap-3">
              <Button onClick={handleGenerate} disabled={generating} className="gap-2">
                {generating ? (
                  <><Loader2 className="size-4 animate-spin" />Generating Mapping...</>
                ) : (
                  <><Sparkles className="size-4" />Generate AI Mapping</>
                )}
              </Button>
              {mappingResult && (
                <span className="flex items-center gap-1.5 text-xs text-emerald-700">
                  <CheckCircle2 className="size-3.5" />Mapping generated
                </span>
              )}
            </div>

            {/* Mapping workspace */}
            {mappingResult && (
              <>
                <Separator />
                <div className="flex flex-col gap-4">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <FileText className="size-4" />
                    AI-Generated Mapping
                    <Badge variant="secondary" className="text-xs">Editable</Badge>
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <EditableField
                        label="Use Case Name"
                        value={displayed.useCaseName ?? ""}
                        onChange={(v) => updateField("useCaseName", v)}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <EditableField
                        label="Candidate AI Solution"
                        value={displayed.candidateSolution ?? ""}
                        onChange={(v) => updateField("candidateSolution", v)}
                        multiline
                      />
                    </div>
                    <EditableField
                      label="AI Type"
                      value={displayed.aiType ?? ""}
                      onChange={(v) => updateField("aiType", v)}
                    />
                    <EditableField
                      label="Automation Type"
                      value={displayed.automationType ?? ""}
                      onChange={(v) => updateField("automationType", v)}
                    />
                    <EditableField
                      label="Expected Time Saving"
                      value={displayed.expectedTimeSaving ?? ""}
                      onChange={(v) => updateField("expectedTimeSaving", v)}
                    />
                    <EditableField
                      label="Expected Cost Saving"
                      value={displayed.expectedCostSaving ?? ""}
                      onChange={(v) => updateField("expectedCostSaving", v)}
                    />
                    <EditableField
                      label="Expected Risk Reduction"
                      value={displayed.expectedRiskReduction ?? ""}
                      onChange={(v) => updateField("expectedRiskReduction", v)}
                    />
                    <EditableField
                      label="Suggested Data Classification"
                      value={displayed.suggestedDataClassification ?? ""}
                      onChange={(v) => updateField("suggestedDataClassification", v)}
                    />
                    <EditableField
                      label="AI Owner"
                      value={displayed.aiOwner ?? ""}
                      onChange={(v) => updateField("aiOwner", v)}
                    />
                  </div>

                  {/* Required Data */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Required Data</span>
                    <div className="flex flex-wrap gap-1.5">
                      {displayed.requiredData?.map((d) => (
                        <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Feasibility Questions */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Initial Feasibility Questions</span>
                    <ul className="flex flex-col gap-1">
                      {displayed.feasibilityQuestions?.map((q, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="size-5 shrink-0 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center mt-0.5 font-medium">{i + 1}</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Suggested Dependencies */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Suggested Dependencies</span>
                    <div className="flex flex-wrap gap-1.5">
                      {displayed.suggestedDependencies?.map((d) => (
                        <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={handleSave} disabled={saving} className="gap-2">
                    {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                    Save Mapping
                  </Button>
                  <Button variant="outline" className="gap-2" onClick={() => toast.info("Sent for feasibility review.")}>
                    <Send className="size-4" />
                    Send for Feasibility Review
                  </Button>
                  <Button variant="outline" className="gap-2" onClick={() => toast.warning("Marked as duplicate.")}>
                    <Copy className="size-4" />
                    Mark as Duplicate
                  </Button>
                  <Button variant="outline" className="gap-2 text-destructive hover:text-destructive" onClick={() => toast.error("Use case rejected.")}>
                    <Ban className="size-4" />
                    Reject
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
